import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import { Switch } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import CircularProgress from '@material-ui/core/CircularProgress';
import { ClientStatus, fetchClientStatuses, fetchServiceStatuses, invokeServiceStatusChange, ServiceStatus } from "../api-client/client"
import Grid from "@material-ui/core/Grid";
import ErrorMessage from "../components/ErrorMessage";
import Button from "@material-ui/core/Button";
import AdapterLink from "../components/AdapterLink";
import Collapse from "@material-ui/core/Collapse";

interface AccessSwitchesPageProps {
    isAdmin: boolean
}

const AccessSwitchesPage: React.FC<AccessSwitchesPageProps> = (props) => {
    const { isAdmin } = props;
    const [ services, setServices ] = useState<ServiceStatus[]>([]);
    const [ clientStatus, setClientStatus ] = useState<ClientStatus[]>([]);
    const [ errorMsg, setErrorMsg ] = useState<string>();
    const [ listReady, setListReady ] = useState<boolean>(false);
    const [ servicesBeingRefreshed, setServicesBeingRefreshed ] = useState<string[]>([]);

    const handleSwitchChange = (serviceName: string, checked: boolean) => {
        setServicesBeingRefreshed([ serviceName, ...servicesBeingRefreshed ]);
        invokeServiceStatusChange(serviceName, checked)
            .then((service) => new Promise((res, rej) => setTimeout(res, 300)))
            .then(() => {
                // @ts-ignore
                services.find(s => s.serviceName === serviceName).emergency = checked;
                setServices(services);
                setServicesBeingRefreshed(servicesBeingRefreshed.filter(s => s !== serviceName));
            })
            .catch(error => {
                setErrorMsg(error.message);
            });
    };

    const isServiceBeingRefreshed = (serviceName: string) => servicesBeingRefreshed.some(s => s === serviceName);

    const isServiceClientInAutomaticEmergency = (service: ServiceStatus) : boolean => {
        const matchingClients = clientStatus.filter(client => (client.clientName === service.clientName) && client.automaticEmergency);
        return matchingClients.length > 0;
    }

    useEffect(() => {
        let timerId: any
        const controller = new AbortController();
        fetchServiceStatuses(controller.signal).then(fetchedServiceStatues => {
            setServices(fetchedServiceStatues)
            timerId = setTimeout(() => { setListReady(true) }, 50)
        }).catch(error => {
            setErrorMsg(error.message);
        })
        return () => {
            controller.abort();
            clearTimeout(timerId)
        }
    }, [ setServices ]);

    useEffect(() => {
        const controller = new AbortController();
        fetchClientStatuses(controller.signal).then(fetchedClientStatuses => {
            setClientStatus(fetchedClientStatuses)
        }).catch(error => {
            setErrorMsg(error.message);
        })
        return () => {
            controller.abort();
        }
    }, [ ]);

    return (
        <Box mt={ 4 }>
            <Paper>
                <Box p={ 3 } pt={ 4 }>
                    <Typography component="h2" variant="h5" color="primary" gutterBottom>
                        Emergency Access Control
                    </Typography>
                    <Typography variant={ "body2" }>
                        When status is set to normal mode calls will be forwarded to SalesForce to verify users permissions to access given service. When status
                        is set to emergency mode calls will not be forwarded to SalesForce and will grant open access to everybody.
                    </Typography>

                    {
                        clientStatus.some(client => client.automaticEmergency) ?
                            <Typography variant={ "body2" } style={ { color: "orange", fontWeight: "bolder" } }>
                                Automatic emergency mode for one or multiple sources have been activated by the system.
                                Affected sources are:
                                {
                                    clientStatus.filter(client => client.automaticEmergency).map(openClient => (
                                        <li key={openClient.clientName}> { openClient.clientName } </li>
                                    ))
                                }
                            </Typography>
                            :
                            null
                    }

                    <Box pt={ 2 }>
                        { errorMsg ? <ErrorMessage message={ errorMsg }/> : (

                            services.length > 0 ? (
                                <Table size={ "small" } id={ "service-status-table" }>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell variant={ "head" }>Service</TableCell>
                                            <TableCell variant={ "head" }>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        { services.map(row => (
                                            <TableRow key={ row.serviceName } data-testid={ "row-" + row.serviceName }>
                                                <TableCell>
                                                    { row.serviceName }
                                                </TableCell>
                                                <TableCell>
                                                    <FormControlLabel
                                                        data-testid={ "switch-container-" + row.serviceName }
                                                        control={ <Switch
                                                            checked={ row.emergency }
                                                            color={ "primary" }
                                                            disabled={ (isServiceBeingRefreshed(row.serviceName) || !isAdmin) || (isServiceClientInAutomaticEmergency(row)) }
                                                            onChange={ (event, checked) => handleSwitchChange(row.serviceName, checked) }
                                                            inputProps={ { "data-testid": "switch-" + row.serviceName } as InputHTMLAttributes<HTMLInputElement> }
                                                        /> }
                                                        label={
                                                            <Collapse in={ !isServiceBeingRefreshed(row.serviceName) && listReady }>
                                                                <Box fontStyle={ "italic" } minWidth={ 120 }><Typography
                                                                    variant={ "caption" }>{
                                                                    isServiceClientInAutomaticEmergency(row)
                                                                        ? "Automatic Emergency mode"
                                                                        : row.emergency
                                                                            ? "Emergency mode"
                                                                            : "Normal mode"
                                                                }</Typography></Box>
                                                            </Collapse>
                                                        }/>
                                                </TableCell>
                                            </TableRow>
                                        )) }
                                    </TableBody>
                                </Table>
                            ) : <Grid container justifyContent={ "center" }><CircularProgress/></Grid>) }
                    </Box>
                    { isAdmin &&
                        <Box mt={ 2.5 }>
                            <Button variant="outlined" color="primary" component={ AdapterLink } to="/access/create">
                                Add service
                            </Button>
                            <Box display="inline" pl={ 1 }>
                                <Button variant="outlined" color="primary" component={ AdapterLink } to="/response-messages" id="configure-resp-msg-button">
                                    Configure response messages
                                </Button>
                            </Box>
                        </Box>
                    }
                </Box>
            </Paper>
        </Box>
    );
};

export default AccessSwitchesPage;
